import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Play, Clock, BookOpen, ArrowRight, AlertCircle, Calendar } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MyCoursesPage = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user || !token) {
      navigate('/prisijungti?redirect=/mano-kursai');
      return;
    }
    loadMyCourses();
  }, [user, token, navigate]);

  const loadMyCourses = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/my-courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourses(response.data);
    } catch (error) {
      console.error('Error loading courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('lt-LT', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getDaysRemaining = (expiresAt) => {
    const now = new Date();
    const expires = new Date(expiresAt);
    const diffTime = expires - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#4CAF50] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="my-courses-page" className="min-h-screen bg-[#FAFAF8]">
      {/* Header */}
      <section className="bg-white py-12 border-b border-slate-100">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <h1 className="text-3xl md:text-4xl font-['Poppins'] font-bold text-[#2D3748] mb-2">
            Mano kursai
          </h1>
          <p className="text-[#718096]">
            Čia rasite visus savo įsigytus video kursus
          </p>
        </div>
      </section>

      {/* Courses */}
      <section className="py-12">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          {courses.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {courses.map((purchase) => {
                const daysRemaining = getDaysRemaining(purchase.expires_at);
                const isExpired = daysRemaining <= 0;
                const isExpiringSoon = daysRemaining > 0 && daysRemaining <= 30;

                return (
                  <div
                    key={purchase.id}
                    data-testid={`my-course-${purchase.course_id}`}
                    className={`bg-white rounded-2xl overflow-hidden border ${
                      isExpired ? 'border-red-200 opacity-75' : 'border-slate-100'
                    }`}
                  >
                    {/* Thumbnail */}
                    <div className={`${purchase.course?.lightColor || 'bg-[#E8F5E9]'} h-40 flex items-center justify-center relative`}>
                      <div className={`w-16 h-16 ${purchase.course?.color || 'bg-[#4CAF50]'} rounded-full flex items-center justify-center`}>
                        <Play className="w-8 h-8 text-white ml-1" />
                      </div>
                      
                      {isExpired && (
                        <div className="absolute top-3 right-3 bg-red-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                          Pasibaigė
                        </div>
                      )}
                      {isExpiringSoon && !isExpired && (
                        <div className="absolute top-3 right-3 bg-[#FF9F43] text-white px-3 py-1 rounded-full text-xs font-medium">
                          Baigiasi greitai
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="p-5">
                      <h3 className="text-lg font-['Poppins'] font-semibold text-[#2D3748] mb-2">
                        {purchase.course?.title || 'Kursas'}
                      </h3>

                      {/* Stats */}
                      <div className="flex items-center space-x-4 mb-4 text-sm text-[#718096]">
                        <div className="flex items-center">
                          <Clock className="w-4 h-4 mr-1" />
                          {purchase.course?.duration || '—'}
                        </div>
                        <div className="flex items-center">
                          <BookOpen className="w-4 h-4 mr-1" />
                          {purchase.course?.lessons_count || 0} pamokų
                        </div>
                      </div>

                      {/* Expiration */}
                      <div className={`flex items-center space-x-2 text-sm mb-4 ${
                        isExpired ? 'text-red-500' : isExpiringSoon ? 'text-[#FF9F43]' : 'text-[#718096]'
                      }`}>
                        <Calendar className="w-4 h-4" />
                        {isExpired ? (
                          <span>Prieiga pasibaigė {formatDate(purchase.expires_at)}</span>
                        ) : (
                          <span>Galioja iki {formatDate(purchase.expires_at)} ({daysRemaining} d.)</span>
                        )}
                      </div>

                      {/* Action */}
                      {!isExpired ? (
                        <Link
                          to={`/kursai/${purchase.course_id}/ziureti`}
                          className="flex items-center justify-center space-x-2 w-full bg-[#4CAF50] text-white hover:bg-[#43A047] rounded-full py-3 font-medium transition-colors"
                        >
                          <Play className="w-5 h-5" />
                          <span>Žiūrėti kursą</span>
                        </Link>
                      ) : (
                        <Link
                          to="/kursai"
                          className="flex items-center justify-center space-x-2 w-full bg-slate-100 text-[#718096] hover:bg-slate-200 rounded-full py-3 font-medium transition-colors"
                        >
                          <span>Įsigyti iš naujo</span>
                          <ArrowRight className="w-5 h-5" />
                        </Link>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* Empty State */
            <div className="text-center py-16">
              <div className="w-24 h-24 bg-[#E3F2FD] rounded-full flex items-center justify-center mx-auto mb-6">
                <Play className="w-12 h-12 text-[#5B9BD5]" />
              </div>
              <h3 className="text-2xl font-['Poppins'] font-semibold text-[#2D3748] mb-3">
                Dar neturite kursų
              </h3>
              <p className="text-[#718096] mb-8 max-w-md mx-auto">
                Įsigykite video kursų ir mokykitės savo tempu – bet kada, bet kur, 2 metus.
              </p>
              <Link
                to="/kursai"
                className="inline-flex items-center bg-[#4CAF50] text-white hover:bg-[#43A047] rounded-full px-8 py-4 font-semibold transition-colors"
              >
                Peržiūrėti kursus
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Help Section */}
      {courses.length > 0 && (
        <section className="py-12 bg-white border-t border-slate-100">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
            <div className="bg-[#FFF3E0] rounded-2xl p-6 flex items-start space-x-4">
              <AlertCircle className="w-6 h-6 text-[#FF9F43] flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-[#2D3748] mb-1">Turite klausimų?</h3>
                <p className="text-sm text-[#718096]">
                  Jei kyla problemų su video peržiūra ar turite kitų klausimų, 
                  <Link to="/kontaktai" className="text-[#FF9F43] hover:underline ml-1">
                    susisiekite su mumis
                  </Link>.
                </p>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default MyCoursesPage;
